import { Link } from 'react-router-dom';
import type { TranslationKey } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { Icon, type IconName } from './Icon';

/**
 * The footer from the supplied design: brand column, three link columns, and contact
 * details, stacking to one column on mobile (PRD §28).
 *
 * The link lists are static route names rather than CMS data on purpose — these are the
 * publication's own pages, not content. Categories and districts *are* content, and
 * Phase 8 passes them in once the homepage aggregate is wired.
 */
const QUICK_LINKS: { key: TranslationKey; segments: string[] }[] = [
  { key: 'nav.home', segments: [] },
  { key: 'nav.videos', segments: ['videos'] },
  { key: 'section.trending', segments: ['trending'] },
  { key: 'section.liveUpdates', segments: ['live'] },
  { key: 'newsletter.title', segments: ['newsletter'] },
];

const CATEGORY_LINKS: { key: TranslationKey; segments: string[] }[] = [
  { key: 'nav.state', segments: ['state'] },
  { key: 'nav.national', segments: ['national'] },
  { key: 'nav.sports', segments: ['sports'] },
  { key: 'nav.health', segments: ['healthcare'] },
  { key: 'nav.jobs', segments: ['jobs'] },
];

const LEGAL_LINKS: { key: TranslationKey; segments: string[] }[] = [
  { key: 'footer.about', segments: ['about'] },
  { key: 'footer.privacy', segments: ['privacy'] },
  { key: 'footer.terms', segments: ['terms'] },
  { key: 'footer.advertise', segments: ['advertise'] },
];

const SOCIALS: { name: IconName; href: string; label: string }[] = [
  { name: 'facebook', href: 'https://facebook.com/', label: 'Facebook' },
  { name: 'x', href: 'https://x.com/', label: 'X' },
  { name: 'instagram', href: 'https://instagram.com/', label: 'Instagram' },
  { name: 'youtube', href: 'https://youtube.com/', label: 'YouTube' },
];

function Column({
  title,
  links,
}: {
  title: string;
  links: { key: TranslationKey; segments: string[] }[];
}) {
  const { t, path } = useLocale();
  return (
    <div>
      {/*
        `h2`, not `h4`: a footer column is a top-level section of the page, and its rank
        has to be honest about that. As an `h4` it skipped a level on any page whose last
        heading before the footer was an `h2` — the search results page, for one.
      */}
      <h2 className="mb-4 font-sans text-sm font-extrabold tracking-wide text-ink">{title}</h2>
      <ul className="flex flex-col gap-2.5">
        {links.map((link) => (
          <li key={link.key}>
            <Link
              to={path(...link.segments)}
              className="text-sm text-body transition hover:text-red"
            >
              {t(link.key)}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Footer() {
  const { t } = useLocale();
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-line bg-white px-gutter pt-9" data-print-hide>
      <div className="grid gap-6 border-b border-line pb-8 md:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr_1fr]">
        <div>
          {/* The same lockup as the masthead — lazy here, because the footer is below
              every fold the site has. */}
          <picture>
            <source
              srcSet="/brand/lockup-220.webp 1x, /brand/lockup-440.webp 2x"
              type="image/webp"
            />
            <img
              src="/brand/lockup-220.png"
              srcSet="/brand/lockup-220.png 1x, /brand/lockup-440.png 2x"
              alt={`${t('brand.mark')} ${t('brand.name')}`}
              width={140}
              height={97}
              loading="lazy"
              decoding="async"
              className="mb-3.5 h-[72px] w-auto"
            />
          </picture>
          <p className="mb-4 max-w-[230px] text-sm text-body">{t('footer.blurb')}</p>
          <ul className="flex gap-3.5 text-nav-idle">
            {SOCIALS.map((social) => (
              <li key={social.name}>
                <a
                  href={social.href}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={social.label}
                  className="block transition hover:text-red"
                >
                  <Icon name={social.name} size={16} />
                </a>
              </li>
            ))}
          </ul>
        </div>

        <Column title={t('footer.quickLinks')} links={QUICK_LINKS} />
        <Column title={t('footer.categories')} links={CATEGORY_LINKS} />
        <Column title={t('footer.legal')} links={LEGAL_LINKS} />
      </div>

      <div className="flex flex-col gap-2 py-4 text-xs text-muted md:flex-row md:items-center md:justify-between">
        <span>
          © {year} {t('brand.name')}. {t('footer.rights')}
        </span>
        <span className="text-body">{t('footer.madeIn')}</span>
      </div>
    </footer>
  );
}
